import React, { useState } from "react";
import { Image } from "react-bootstrap";
import { ModalProps } from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";

import "./GemInfoModal.scss";
import CloseRoundedIcon from "../../../icons/CloseRoundedIcon";
import HyperCoin from "../../../HyperCoin/HyperCoin";
import PrimaryActionButton from "../../../Buttons/PrimaryActionButton/PrimaryActionButton";
import GhostButton from "../../GhostButton/GhostButton";
import Gem from "../../../Gem/Gem";
import FlameIcon from "../../../icons/FlameIcon";
import PersonDuoIcon from "../../../icons/PersonDuoIcon";
import GiftSolidIcon from "../../../icons/GiftSolidIcon";
import FilledButton from "../../FilledButton/FilledButton";
import ImageHourglass from "../../../../images/hourglass.svg";
import ImageWarning from "../../../../images/warning-alert-circle-orange.svg";
import CollectGemWinHprModal from "../CollectGemWinHprModal/CollectGemWinHprModal";
import FixedFooter from "../../../FixedFooter/FixedFooter";
import DownloadAppPrompt from "../../../DownloadAppPrompt/DownloadAppPrompt";
import useDisclosure from "../../../../hooks/useDisclosure";
import useHprFiatFormatter from "../../../../hooks/useHprFiatFormatter";

interface GemInfoModalProps extends ModalProps {
  balance?: string;
}

const WIN_AMOUNT_HPR = 1000 as const;
const FIAT_DISPLAY_PRECISION = 5;

function GemInfoModal({ onHide, show, balance }: GemInfoModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const {
    isOpen: isDownloadAppPromptOpen,
    onOpen: openDownloadAppPrompt,
    onClose: closeDownloadAppPrompt,
  } = useDisclosure();
  const { t } = useTranslation("common");

  const { isFiatDisplay, formatHprToFiatValue } = useHprFiatFormatter();
  const winAmountDisplay = formatHprToFiatValue(WIN_AMOUNT_HPR, FIAT_DISPLAY_PRECISION);

  if (!show) return null;

  return (
    <div className="fixed-modal MobileOnly" role="dialog" aria-modal="true">
      <div className="content-box">
        <div className="hero">
          <p className="close-btn-container d-flex justify-content-end">
            <button aria-label="close" onClick={onHide}>
              <CloseRoundedIcon fill="white" />
            </button>
          </p>
          <p className="d-flex justify-content-center">
            <span className="balance">
              <Gem width={25} height={25} />
              <span>{balance}</span>
            </span>
          </p>
          <p className="hero-text">{t("gem_are_free_rewards_you_earn_key")}</p>
        </div>
        <div className="info-container">
          <div className="info-card">
            <h2>
              {t("how_to_get_gem")} <Gem width={14} height={14} />
            </h2>
            <div className="item">
              <div className="col-one">
                <div className="icon-container yellow-bg">
                  <FlameIcon fill="#F2AD3D" />
                </div>
              </div>
              <div className="col-two">
                <h5>{t("keep_daily_streaks")}</h5>
                <p className="content">
                  {t("come_back_everyday_to_earn_gem_and_keep_your_streaks")}
                </p>
              </div>
            </div>
            <div className="item">
              <div className="col-one">
                <div className="icon-container green-bg">
                  <PersonDuoIcon fill="#389E0D" />
                </div>
              </div>
              <div className="col-two">
                <h5>{t("invite_friends")}</h5>
                <p className="content">
                  {t("get")}{" "}
                  <strong>
                    <Gem width={18} height={18} /> 10 {t("gem")}{" "}
                  </strong>{" "}
                  {t("for_each_referral_and_an_extra")}{" "}
                  <strong>
                    {" "}
                    <Gem width={18} height={18} /> 200 {t("gem")}
                  </strong>{" "}
                  {t("for_referring")} <strong>5</strong>{" "}
                  {t("friends_in_a_week")}
                </p>
              </div>
            </div>
            <div className="item">
              <div className="col-one">
                <div className="icon-container purple-bg">
                  <GiftSolidIcon fill="#AB66FF" />
                </div>
              </div>
              <div className="col-two">
                <h5>{t("complete_quests")}</h5>
                <p className="content">
                  {t("get_rewarded_with_free_gem_when_you_complete_quests")}
                </p>
              </div>
            </div>
            <div className="item" style={{ marginBottom: 0 }}>
              <div className="col-one">
                <div className="icon-container blue-bg">
                  <div className="gem-hpr">
                    <Gem width={18} height={16} className="gem" />
                    <HyperCoin width={16} height={16} className="hpr" />
                  </div>
                </div>
              </div>
              <div className="col-two">
                <h5>
                  {isFiatDisplay
                    ? t("collect_gem_win_reward")
                    : t("collect_gem_win_hpr")}
                </h5>
                <p className="content mb-1">
                  {isFiatDisplay
                    ? t("stand_a_chance_to_win")
                    : t("get_the_opportunity_to_win")}{" "}
                  {!isFiatDisplay && <HyperCoin width={14} height={14} />}
                  {isFiatDisplay ? winAmountDisplay : WIN_AMOUNT_HPR}
                </p>
                <FilledButton
                  style={{
                    fontSize: 12,
                    height: 24,
                    padding: "4px 8px",
                    borderRadius: 8,
                  }}
                  onClick={() => setIsOpen(true)}
                >
                  {t("learn_more")}
                </FilledButton>
              </div>
            </div>
          </div>
          <div className="alert" role="alert">
            <Image src={ImageWarning} alt="warning" width={11} height={11} />
            <p>
              {t("reset_your_countdown_to")}{" "}
              <strong>{t("days_count", { count: 7 })}</strong> {t("by_earning")}{" "}
              <Gem width={10} height={9} /> {t("gem_everyday")}. {t("the")}{" "}
              <Image
                src={ImageHourglass}
                alt="hourglass"
                width={7}
                height={12}
              />{" "}
              {t("icon_would_appear_when_time_is_running_out")}!
            </p>
          </div>
        </div>
      </div>
      <FixedFooter className="px-3 pb-4">
        <PrimaryActionButton text={t("got_it")} onClick={onHide} />
        <GhostButton className="earn-gem-btn" onClick={openDownloadAppPrompt}>
          {t("earn_gem")}
        </GhostButton>
      </FixedFooter>
      <CollectGemWinHprModal show={isOpen} onHide={() => setIsOpen(false)} />
      <DownloadAppPrompt
        isOpen={isDownloadAppPromptOpen}
        onOpen={openDownloadAppPrompt}
        onClose={closeDownloadAppPrompt}
      />
    </div>
  );
}

export default GemInfoModal;
